import React, { useState, useEffect } from "react";
import Clinicas from "../Components/Configuracion/Clinica";
import Company from "../Components/Configuracion/Company/Company";
import Decuentos from "../Components/Configuracion/Descuentos/Descuentos";
import Doctor from "../Components/Configuracion/Doctor";
import Especialidad from "../Components/Configuracion/Especialidad/Especialidad";
import ProcedimientoPrecio from "../Components/Configuracion/ProcedimientoPrecio/ProcedimientoPrecio";
import ProcMedico from "../Components/Configuracion/ProcMedico";
import Seguros from "../Components/Configuracion/Seguros/Seguros";
import Promocion from "../Components/Configuracion/Promocion/Promocion";
import Plan from "../Components/Configuracion/Planes/PlanIndex";
import UserIndex from "../Components/Configuracion/Usuarios/UserIndex";
import RolesIndex from "../Components/Configuracion/RolesPermisos/RolesIndex";
import SecurityAccessIndex from "../Components/Configuracion/SecurityAccess/SecurityAccessIndex";
import TiposDocumentoResultado from "../Components/Configuracion/TiposDocumentoResultado/TiposDocumentoResultado";
import Apariencia from "../Components/Configuracion/Apariencia/Apariencia";
import PanelConfiguracion from "../Components/UI/Configuracion/PanelConfiguracion";
import usePermissions from "../hook/usePermissions";
import { usePageSubtitulo } from "../context/PageHeaderContext";
import "../../src/styles/configuracion-tabs.css";


// RBAC-CUTOVER-03: el acceso a TODA la seccion Configuracion (no solo a un
// tab) ya se decide antes de llegar aqui -- ver ADMIN_AREA_PERMISSIONS en
// Layout/Admin.js (route guard) y Components/Sidebar/Sidebar.js (visibilidad
// del link), ambos con el mismo catalogo. Este componente solo gatea cada
// TAB individualmente con su permiso propio (ver canView* abajo).

export default function Configuracion() {
  const [activeTab, setActiveTab] = useState("clinicas");
  const { loading: permissionsLoading, hasPermission } = usePermissions();
  const canViewCompany = hasPermission("company.view");
  const canViewClinicas = hasPermission("clinics.view") || hasPermission("clinics.view_list");
  const canViewDoctores = hasPermission("doctors.view");
  const canViewEspecialidades = hasPermission("especialidades.view");
  const canViewAdministrativo = hasPermission("users.view_list");
  const canViewSeguros = hasPermission("seguros.view");
  const canViewTarifarios = hasPermission("procedimientosprecios.view");
  const canViewPromociones = hasPermission("promociones.view");
  const canViewPlanes = hasPermission("planes.view");
  // RBAC-CUTOVER-09: descuentos.view -- el tab sigue sin boton visible
  // (comentado, sin relanzar la funcionalidad), pero el contenido y el
  // permission-set quedan correctos por si se reactiva.
  const canViewDescuentos = hasPermission("descuentos.view");
  const canViewRoles = hasPermission("roles.view");
  // RESULTADOS-05: tab de administración de tipos de documento de resultados.
  const canViewTiposDocResultado = hasPermission("resultados.tipo_documento.manage");
  const canViewSecurityAccess = hasPermission("security.access_log.view") && hasPermission("security.audit.view") && hasPermission("support.context.manage");

  // RBAC-CUTOVER-03/07: reemplaza nivel_user_id===1 -- el tab por defecto/de
  // respaldo se deriva de los permisos reales del actor, nunca de un campo
  // legacy binario. RBAC-CUTOVER-07 cierra el PERMISSION_CATALOG_GAP de
  // Doctores/Especialidades/Seguros/Tarifarios/Promociones/Planes: ya
  // participan de este orden con su propio permiso *.view.
  const tabPermission = {
    company: canViewCompany,
    clinicas: canViewClinicas,
    doctores: canViewDoctores,
    especialidades: canViewEspecialidades,
    asesores: canViewAdministrativo,
    seguros: canViewSeguros,
    descuento: canViewDescuentos,
    procedimientoPrecio: canViewTarifarios,
    promocion: canViewPromociones,
    planes: canViewPlanes,
    rolesPermisos: canViewRoles,
    tiposDocResultado: canViewTiposDocResultado,
    securityAccess: canViewSecurityAccess,
  };
  const firstAllowedTab = Object.keys(tabPermission).find((tab) => tabPermission[tab]);

  useEffect(() => {
    if (permissionsLoading) return;
    // Si el tab activo tiene un permiso propio y el actor no lo tiene,
    // reubicar en el primer tab que si le corresponda.
    if (Object.prototype.hasOwnProperty.call(tabPermission, activeTab) && !tabPermission[activeTab] && firstAllowedTab) {
      setActiveTab(firstAllowedTab);
    }
  }, [activeTab, permissionsLoading, canViewCompany, canViewClinicas, canViewDoctores, canViewEspecialidades, canViewAdministrativo, canViewSeguros, canViewTarifarios, canViewPromociones, canViewPlanes, canViewRoles, canViewSecurityAccess]); // eslint-disable-line react-hooks/exhaustive-deps
  
  // Mantiene la compatibilidad: se pasa tabs como antes
  const tabs = {
    company: activeTab === "company",
    clinicas: activeTab === "clinicas",
    doctores: activeTab === "doctores",
    especialidades: activeTab === "especialidades",
    asesores: activeTab === "asesores",
    seguros: activeTab === "seguros",
    descuento: activeTab === "descuento",
    procedimientoPrecio: activeTab === "procedimientoPrecio",
    promocion: activeTab === "promocion",
    planes: activeTab === "planes",
    rolesPermisos: activeTab === "rolesPermisos",
    tiposDocResultado: activeTab === "tiposDocResultado",
    securityAccess: activeTab === "securityAccess",
  };

  const handleTabs = (tab) => {
    setActiveTab(tab);
  };

  // SISTEMA-DE-DISENO: las secciones se declaran como DATOS y agrupadas por
  // alcance. Antes eran trece bloques de JSX casi idénticos, cada uno con su
  // propia condición de permiso repetida en dos sitios (el botón y el
  // contenido); ahora la condición se escribe una vez.
  //
  // "Descuento" sigue sin aparecer en el índice: su botón llevaba comentado
  // desde RBAC-CUTOVER-09. No se resucita una funcionalidad al migrar una
  // pantalla -- pero tampoco se rompe: sigue siendo alcanzable (`oculta`), que
  // es exactamente lo que pasaba antes, porque `firstAllowedTab` puede
  // resolver a "descuento" para quien sólo tenga ese permiso.
  const SECCIONES = [
    { clave: "company", etiqueta: "Company", permitida: canViewCompany, contenido: Company },
    { clave: "clinicas", etiqueta: "Clínicas", permitida: canViewClinicas, contenido: Clinicas },
    { clave: "doctores", etiqueta: "Doctores", permitida: canViewDoctores, contenido: Doctor },
    { clave: "especialidades", etiqueta: "Especialidades", permitida: canViewEspecialidades, contenido: Especialidad },
    { clave: "asesores", etiqueta: "Administrativo", permitida: canViewAdministrativo, contenido: UserIndex },
    { clave: "seguros", etiqueta: "Seguros", permitida: canViewSeguros, contenido: Seguros },
    { clave: "descuento", etiqueta: "Descuento", permitida: canViewDescuentos, contenido: Decuentos, oculta: true },
    { clave: "procedimientoPrecio", etiqueta: "Tarifarios", permitida: canViewTarifarios, contenido: ProcedimientoPrecio },
    { clave: "promocion", etiqueta: "Promociones", permitida: canViewPromociones, contenido: Promocion },
    { clave: "planes", etiqueta: "Planes", permitida: canViewPlanes, contenido: Plan },
    { clave: "rolesPermisos", etiqueta: "Roles y permisos", permitida: canViewRoles, contenido: RolesIndex },
    { clave: "tiposDocResultado", etiqueta: "Tipos de documento", permitida: canViewTiposDocResultado, contenido: TiposDocumentoResultado },
    { clave: "securityAccess", etiqueta: "Seguridad", permitida: canViewSecurityAccess, contenido: SecurityAccessIndex },
  ];

  const grupos = [
    {
      titulo: "La clínica",
      alcance: "Lo que cambies aquí lo ve todo el mundo",
      secciones: SECCIONES.filter((s) => s.permitida && !s.oculta).map(({ clave, etiqueta }) => ({ clave, etiqueta })),
    },
    {
      titulo: "Tus preferencias",
      alcance: "Sólo afectan a tu cuenta",
      // Apariencia NO tiene permiso: es una preferencia de quien mira, y quien
      // llegó hasta aquí ya pasó el guard de la ruta.
      secciones: [{ clave: "apariencia", etiqueta: "Apariencia" }],
    },
  ].filter((g) => g.secciones.length > 0);

  const seccionActiva = SECCIONES.find((s) => s.clave === activeTab && s.permitida);
  const Contenido = seccionActiva?.contenido;

  const etiquetaActiva =
    activeTab === "apariencia"
      ? "Apariencia"
      : SECCIONES.find((s) => s.clave === activeTab)?.etiqueta || activeTab;

  usePageSubtitulo(etiquetaActiva);

  return (
    <div className="page-content">
      <div className="container-fluid">
        {/* Encabezado */}
        <div className="page-title-box d-sm-flex align-items-center justify-content-between">
          <h4 className="mb-sm-0 font-size-18">Configuración</h4>
          <div className="page-title-right">
            <ol className="breadcrumb m-0">
              <li className="breadcrumb-item">Configuración</li>
              <li className="breadcrumb-item active">{etiquetaActiva}</li>
            </ol>
          </div>
        </div>

        <PanelConfiguracion grupos={grupos} activa={activeTab} onCambiar={handleTabs}>
          {activeTab === "apariencia" ? (
            <Apariencia />
          ) : Contenido ? (
            <Contenido tabs={tabs} />
          ) : (
            !permissionsLoading &&
            !firstAllowedTab && (
              <p className="text-muted mb-0">No tiene permisos para ver ningún módulo de Configuración.</p>
            )
          )}
        </PanelConfiguracion>
      </div>
    </div>
  );
}
